// ═══════════════════════════════════════════════
// FracSight 压裂模拟 — 共享类型定义
// ═══════════════════════════════════════════════

import type { NaturalFractureDesignDTO, NaturalFractureSourceMode } from './naturalFractureDesign';

/** 时间步: 裂缝扩展过程中单个时刻的快照 */
export type TimeStep = {
  step: number;
  t_s: number;               // 累积泵注时间 (s)
  L_m: number;               // 半缝长 (m)
  w0_mm: number;             // 井底最大缝宽 (mm)
  eff_pct: number;           // 压裂液效率 (%)
  net_pressure_mpa: number;  // 净压力 (MPa)
  raw_hydraulic_net_pressure_pa?: number;
  raw_hydraulic_net_pressure_mpa?: number;
  smoothed_hydraulic_net_pressure_pa?: number;
  smoothed_hydraulic_net_pressure_mpa?: number;
  Vi_m3?: number;            // 单翼注入体积 (m³) — PKN
  injected_volume_m3?: number;
  Vf_m3?: number;            // 单翼进缝体积 (m³) — PKN
  h_m?: number;              // 缝高 (m) — P3D
  dt_s?: number;
  flow_rate_m3s?: number;
  fracture_pressure_mpa?: number;
  base_shmin_mpa?: number;
  stress_shadow_mpa?: number;
  local_shmin_mpa?: number;
  length_m?: number;
  half_length_m?: number;
  height_m?: number;
  width_mm?: number;
  is_initiated?: boolean;
  time?: number;
  time_s?: number;
  cluster_id?: string;
  stage_id?: number | string;
  cluster_position_md?: number;
  flow_rate?: number;
  fracture_pressure?: number;
  fracture_pressure_pa?: number;
  stress_shadow?: number;
  stress_shadow_pa?: number;
  local_shmin?: number;
  local_shmin_pa?: number;
  left_wing_length?: number;
  right_wing_length?: number;
  left_wing_length_m?: number;
  right_wing_length_m?: number;
  seed_height_m?: number;
  upper_height_m?: number;
  lower_height_m?: number;
  height_growth_up?: number;
  height_growth_down?: number;
  raw_upper_growth_m?: number;
  raw_lower_growth_m?: number;
  clamped_upper_growth_m?: number;
  clamped_lower_growth_m?: number;
  upper_clamp_active?: boolean;
  lower_clamp_active?: boolean;
  upper_propagation_margin_pa?: number;
  lower_propagation_margin_pa?: number;
  pumping_stage?: string;
  sand_conc_kgm3?: number;
  proppant_type?: string;
  propped_width_m?: number;
  propped_width_mm?: number;
  conductivity?: number;
  proppant_concentration_kgm3?: number;
  stage_flow_rate_m3min?: number;
  wellbore_pressure_pa?: number;
  bottomhole_pressure_mpa?: number;
  pressure_reference_location?: string;
  bottomhole_proppant_concentration_kgm3?: number;
  concentration_definition?: string;
  stage_cumulative_fluid_m3?: number;
  stage_cumulative_proppant_kg?: number;
  cluster_cumulative_proppant_kg?: number;
  cumulative_proppant_kg?: number;
};

/** 沿缝长离散网格 (P3D 输出) */
export type Mesh3D = {
  nx: number;
  x: number[];               // 沿缝长位置 (m)
  h: number[];               // 各 x 处缝高 (m)
  dh_up: number[];           // 进上阻挡层量 (m)
  dh_dn: number[];           // 进下阻挡层量 (m)
  w0_mm: number[];           // 各 x 处中心线缝宽 (mm)
  pn_mpa: number[];          // 各 x 处净压 (MPa)
  zc_tvd: number;            // 缝面垂向中心 TVD (m)
};

/** P3D 三层应力信息 */
export type StressLayers = {
  sigma_m_mpa: number;
  sigma_upper_mpa: number;
  sigma_lower_mpa: number;
  h0_m: number;
  E_gpa: number;
  nu: number;
  kic_m?: number;         // 目标层 K_IC (MPa·√m)
  kic_u?: number;         // 上覆层 K_IC
  kic_b?: number;         // 下伏层 K_IC
};

/** 裂缝有效性评分 */
export type EffectivenessScore = {
  score: number;           // 0-100
  grade: string;           // "优秀"|"良好"|"一般"|"较差"
  breakdown: {
    propped: number;       // 支撑 0-30
    efficiency: number;    // 效率 0-25
    containment: number;   // 缝高遏止 0-20
    uniformity: number;    // 簇均匀性 0-25
  };
};

export type P3DDiagnostic = {
  code: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  affected_stage?: number | string | null;
  affected_cluster?: number | string | null;
  time_s?: number | null;
  value?: unknown;
  threshold?: unknown;
  metadata?: Record<string, unknown>;
};

export type ConfidenceDimension = {
  level: 'High' | 'Medium' | 'Low';
  reasons: string[];
  rule?: string;
};

export type P3DConfidence = {
  input?: ConfidenceDimension;
  model?: ConfidenceDimension;
  calibration?: ConfidenceDimension;
  overall?: ConfidenceDimension;
  prediction?: ConfidenceDimension;
  calibration_status?: 'Not Calibrated' | 'Partially Calibrated' | 'Field Calibrated';
  simulation_mode?: 'Predictive' | 'Calibrated';
};

/** 单簇模拟结果 (多簇模式下) */
export type ClusterResult = {
  cluster_index: number;
  cluster_position_md: number;
  half_length: number;
  height: number;
  w0_mm: number;
  net_pressure_mpa: number;
  efficiency_pct: number;
  allocated_rate_m3s?: number;
  perf_friction_mpa?: number;
  stress_shadow_pa?: number;
  cluster_id?: string;
  stage_id?: number | string;
  time?: number;
  time_s?: number;
  is_initiated?: boolean;
  flow_rate?: number;
  flow_rate_m3s?: number;
  fracture_pressure_pa?: number;
  fracture_pressure_mpa?: number;
  base_shmin_pa?: number;
  base_shmin_mpa?: number;
  local_shmin_pa?: number;
  local_shmin_mpa?: number;
  net_pressure_pa?: number;
  length?: number;
  length_m?: number;
  half_length_m?: number;
  width?: number;
  width_m?: number;
  width_mm?: number;
  left_wing_length?: number;
  right_wing_length?: number;
  left_wing_length_m?: number;
  right_wing_length_m?: number;
  seed_height_m?: number;
  upper_height_m?: number;
  lower_height_m?: number;
  height_growth_up?: number;
  height_growth_down?: number;
  frames?: TimeStep[];
  time_steps?: TimeStep[];
  proppant_type?: string;
  propped_width_m?: number;
  propped_width_mm?: number;
  conductivity?: number;
  proppant_concentration_kgm3?: number;
  surface_model_version?: 'ADAPTIVE_PLANAR_SURFACE_V1' | 'DIRECTIONAL_ADAPTIVE_SURFACE_V1' | 'OFF' | string;
  fracture_surface_frame?: FractureSurfaceFrame | null;
  surface_frames?: FractureSurfaceFrame[];
};

export type FractureSurfaceNode = {
  id: number;
  x: number;
  y?: number;
  z: number;
  nodeXYZ?: [number, number, number];
  localTangent?: [number, number, number];
  localNormal?: [number, number, number];
  localPropagationDirection?: [number, number, number];
  width: number;
  pressure: number;
  netPressure: number;
  Shmin: number;
  SHmax?: number | null;
  Sv?: number | null;
  stressAzimuth?: number | null;
  propagationAngle?: number;
  proppantConcentration?: number;
  proppantArealConcentration?: number;
  effectiveProppedWidth?: number;
  conductivity?: number;
  localStressState?: Record<string, unknown>;
  wing?: 'LEFT' | 'RIGHT' | string;
  propagationState: 'FRONT' | 'INTERIOR' | string;
};

export type FractureSurfaceCell = {
  id: number;
  nodeIds: number[];
  x: number;
  y?: number;
  z: number;
  dx: number;
  dz: number;
  fractured: boolean;
  front: boolean;
  frontType: string;
  width: number;
  pressure: number;
  netPressure: number;
  Shmin: number;
  propagationStatus: string;
  propagationAngle?: number;
  localStressState?: Record<string, unknown>;
  wing?: 'LEFT' | 'RIGHT' | string;
  surfaceProppant?: SurfaceProppantCellStateV1;
  proppantConcentration?: number;
  proppantArealConcentration?: number;
  suspendedProppantMass?: number;
  settledProppantMass?: number;
  bedThickness?: number;
  packingFraction?: number;
  dominantComponent?: string | null;
  componentMasses?: Record<string, Record<string, number>>;
  settlingVelocity?: number;
  localFluidVelocity?: number;
  transportStatus?: string;
  screenoutRisk?: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | string;
  effectiveProppedWidth?: number;
  conductivity?: number;
};

export type SurfaceProppantCellStateV1 = {
  modelVersion: 'SURFACE_2D_V1' | string;
  suspendedMassKg: number;
  settledMassKg: number;
  concentrationKgM3: number;
  arealConcentrationKgM2: number;
  bedMassKg: number;
  bedThicknessM: number;
  packingFraction: number;
  dominantComponent?: string | null;
  componentMasses: Record<string, Record<string, number>>;
  settlingVelocityMps: number;
  localFluidVelocityMps: number;
  transportStatus: string;
  activationTime?: number | null;
  screenoutRisk: string;
  effectiveProppedWidthM: number;
  conductivityDmm: number;
  provenance?: Record<string, unknown>;
};

export type FractureSurfaceWingFrame = {
  wing: 'LEFT' | 'RIGHT' | string;
  halfLength: number;
  tipAzimuth: number;
  nodes: FractureSurfaceNode[];
  faces: number[][];
  cells: FractureSurfaceCell[];
  directionHistory?: Array<Record<string, unknown>>;
  naturalFractureInteractions?: Array<Record<string, unknown>>;
  couplingDiagnostics?: Record<string, unknown>;
  meshQuality?: Record<string, unknown>;
};

export type FractureJunctionV1 = {
  junctionId: string;
  xyz: [number, number, number];
  stageId: number | string;
  clusterId: string;
  wing: 'LEFT' | 'RIGHT' | string;
  mainSurfaceId: string;
  sourceNaturalFractureId: string;
  branchIds: string[];
  pressurePa: number;
  incomingFlowM3s: number;
  mainOutgoingFlowM3s: number;
  branchOutgoingFlowM3s: number;
  massResidualM3s: number;
  activationTime: number;
  interactionType: 'OPEN' | string;
  parentBranchId?: string | null;
  depth?: number;
  modelVersion: 'FRACTURE_JUNCTION_V1' | string;
  proppant?: JunctionProppantStateV1 | null;
  provenance?: Record<string, unknown>;
};

export type JunctionProppantSplitV1 = {
  modelVersion: 'JUNCTION_PROPPANT_PARTITION_ENGINEERING_V1' | string;
  junctionId: string;
  branchId?: string | null;
  componentId: string;
  incomingProppantRateKgS: number;
  mainOutgoingProppantRateKgS: number;
  branchOutgoingProppantRateKgS: number;
  junctionDepositedRateKgS: number;
  residualKgS: number;
  fluidSplitFraction: number;
  transportabilityFactor: number;
  branchEntryFraction: number;
  mainEntryFraction: number;
  junctionRetentionFraction: number;
  apertureParticleRatio?: number;
  branchVelocityMps?: number;
  settlingVelocityMps?: number;
  gravityProjection?: number;
  bridgingRisk: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | string;
  reason: string;
  branchAllocations?: Array<{
    branchId: string;
    branchOutgoingProppantRateKgS: number;
    fluidSplitFraction: number;
    transportabilityFactor: number;
    branchEntryFraction: number;
    junctionRetentionFraction: number;
    apertureParticleRatio: number;
    branchVelocityMps: number;
    settlingVelocityMps: number;
    gravityProjection: number;
    bridgingRisk: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | string;
    reason: string;
  }>;
};

export type JunctionProppantStateV1 = {
  modelVersion: 'JUNCTION_PROPPANT_PARTITION_ENGINEERING_V1' | string;
  junctionId: string;
  depositedMassKg: number;
  depositedByComponentKg: Record<string, number>;
  screenoutRisk: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | string;
  splits: JunctionProppantSplitV1[];
  screenoutModel: string;
};

export type BranchProppantSegmentStateV1 = {
  index: number;
  startSM: number;
  endSM: number;
  lengthM: number;
  areaM2: number;
  suspendedMassKg: number;
  depositedMassKg: number;
  concentrationKgM3: number;
  bedMassKg: number;
  bedThicknessM: number;
  packingFraction: number;
  dominantComponent?: string | null;
  componentMasses: Record<string, Record<string, number | string | null>>;
  localFluidVelocityMps: number;
  gravityProjection: number;
  screenoutRisk: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | string;
  effectiveProppedApertureM: number;
  conductivityDmm: number;
  conductivityProvenance?: Record<string, unknown>;
  granularity: 'BRANCH_SEGMENT' | string;
};

export type BranchProppantStateV1 = {
  modelVersion: 'BRANCH_PROPPANT_STATE_V1' | string;
  transportModelVersion: 'BRANCH_PROPPANT_TRANSPORT_1D_V1' | string;
  branchId: string;
  junctionId: string;
  injectedMassKg: number;
  suspendedMassKg: number;
  depositedMassKg: number;
  transferredOutMassKg?: number;
  concentrationKgM3: number;
  frontPositionM: number;
  componentFrontM: Record<string, number>;
  settlingVelocityMps: Record<string, number>;
  fluidFrontM: number;
  bedMassKg: number;
  bedThicknessM: number;
  packingFraction: number;
  screenoutRisk: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | string;
  effectiveProppedApertureM: number;
  conductivityDmm: number;
  branchConductiveLengthM: number;
  branchConductivityProfile: Array<{ sM: number; conductivityDmm: number }>;
  massResidualKg: number;
  inventoryByComponent: Record<string, Record<string, number>>;
  componentStates: Record<string, {
    injectedMassKg: number;
    suspendedMassKg: number;
    depositedMassKg: number;
    transferredOutMassKg?: number;
    concentrationKgM3: number;
    frontPositionM: number;
    settlingVelocityMps: number;
    bedMassKg: number;
    bedThicknessM: number;
    packingFraction: number;
    screenoutRisk: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | string;
    effectiveProppedApertureM: number;
    conductivityDmm: number;
    massResidualKg: number;
  }>;
  segments: BranchProppantSegmentStateV1[];
  gravityProjection: number;
  hydraulicallyActive: boolean;
  closureStatus: string;
  granularity: 'BRANCH_SEGMENT' | string;
};

export type NaturalFractureBranchV1 = {
  branchId: string;
  junctionId: string;
  parentSurfaceId: string;
  sourceNaturalFractureId: string;
  stageId: number | string;
  clusterId: string;
  wing: 'LEFT' | 'RIGHT' | string;
  depth?: number;
  parentBranchId?: string | null;
  nodesXYZ: Array<[number, number, number]>;
  segments: Array<Record<string, unknown>>;
  direction: [number, number, number];
  currentLengthM: number;
  length: number;
  maxAvailableLengthM: number;
  pressureProfile: number[];
  netPressureProfile: number[];
  pressure: number;
  netPressure: number;
  hydraulicApertureM: number;
  aperture: number;
  flowRateM3s: number;
  flowRate: number;
  storageVolumeM3: number;
  leakoffVolumeM3: number;
  active: boolean;
  geometricExists?: boolean;
  hydraulicallyActive?: boolean;
  arrested: boolean;
  closed: boolean;
  status: 'CLOSED' | 'ACTIVATED' | 'PROPAGATING' | 'OPEN' | 'ARRESTED' | string;
  activationTime: number;
  modelVersion: 'NATURAL_FRACTURE_BRANCH_V1' | string;
  hydraulicsModelVersion?: string;
  apertureModelVersion?: string;
  secondaryIntersections?: Array<Record<string, unknown>>;
  proppant?: BranchProppantStateV1 | null;
  provenance?: Record<string, unknown>;
};

export type FractureNetworkNodeV1 = {
  nodeId: string;
  xyz: [number, number, number];
  type: 'WELL_CLUSTER_INLET' | 'JUNCTION' | 'MAIN_TIP' | 'BRANCH_TIP' | string;
  pressurePa: number;
  incomingFlowM3s: number;
  outgoingFlowM3s: number;
  storageRateM3s: number;
  leakoffRateM3s: number;
  massResidualM3s: number;
  active: boolean;
  provenance?: Record<string, unknown>;
};

export type FractureNetworkEdgeV1 = {
  edgeId: string;
  fromNode: string;
  toNode: string;
  edgeType: 'MAIN_SURFACE' | 'NF_BRANCH' | string;
  lengthM: number;
  hydraulicApertureM: number;
  widthHeightRepresentation: { apertureM: number; hydraulicHeightM: number };
  hydraulicResistance: number;
  flowRateM3s: number;
  pressureDropPa: number;
  storageVolumeM3: number;
  leakoffVolumeM3: number;
  status: 'ACTIVE' | 'HYDRAULICALLY_INACTIVE' | string;
  geometricExists: boolean;
  provenance?: Record<string, unknown>;
};

export type FractureNetworkV1 = {
  modelVersion: 'LIMITED_FRACTURE_NETWORK_V1' | string;
  hydraulicsModelVersion: 'NETWORK_HYDRAULICS_V1' | string;
  nodes: FractureNetworkNodeV1[];
  edges: FractureNetworkEdgeV1[];
  junctions: string[];
  branches: Record<string, string>;
  topologyVersion: number;
  hydraulicVersion: number;
  networkDiagnostics: Record<string, unknown>;
};

export type UnifiedNetworkNodeV1 = {
  nodeId: string;
  xyz: [number, number, number];
  type: 'WELL_CLUSTER_INLET' | 'NETWORK_JUNCTION' | 'PROPAGATION_FRONT' | string;
  pressurePa: number;
  status: string;
  provenance?: Record<string, unknown>;
};

export type UnifiedNetworkEdgeV1 = {
  edgeId: string;
  fromNode: string;
  toNode: string;
  edgeType: 'HYDRAULIC_SURFACE' | 'NATURAL_FRACTURE' | 'ACTIVATED_NF_BRANCH' | string;
  geometry: {
    startXYZ: [number, number, number];
    endXYZ: [number, number, number];
    lengthM: number;
  };
  pressurePa: number;
  flowRateM3s: number;
  hydraulicApertureM: number;
  storageVolumeM3: number;
  leakoffVolumeM3: number;
  proppant: Record<string, unknown>;
  status: string;
  parentEdgeId?: string | null;
  depth: number;
  provenance?: Record<string, unknown>;
};

export type PropagationFrontV1 = {
  frontId: string;
  edgeId: string;
  xyz: [number, number, number];
  direction: [number, number, number];
  type: 'HYDRAULIC_SURFACE' | 'NF_BRANCH' | string;
  pressurePa: number;
  netPressurePa: number;
  stressStatePa: number;
  stressPerturbationPa: number;
  propagationDrivePa: number;
  thresholdPa: number;
  demandVolumeM3: number;
  allocatedVolumeM3: number;
  status: string;
  provenance?: Record<string, unknown>;
};

export type UnifiedFractureNetworkV1 = {
  modelVersion: 'UNIFIED_FRACTURE_NETWORK_CORE_V1' | string;
  propagationModelVersion: 'NETWORK_PROPAGATION_V1' | string;
  stressInteractionModelVersion: 'NETWORK_STRESS_INTERACTION_ENGINEERING_V1' | string;
  status: 'BETA_LIMITED' | string;
  nodes: UnifiedNetworkNodeV1[];
  edges: UnifiedNetworkEdgeV1[];
  junctions: Array<Record<string, unknown>>;
  propagationFronts: PropagationFrontV1[];
  networkDiagnostics: Record<string, unknown>;
};

export type FractureSurfaceFrame = {
  time: number;
  stage: number | string;
  cluster: string;
  model: 'ADAPTIVE_PLANAR_SURFACE_V1' | 'DIRECTIONAL_ADAPTIVE_SURFACE_V1' | string;
  surfaceModelVersion: string;
  wingMode: 'SYMMETRIC_WINGS' | 'ASYMMETRIC_WINGS' | string;
  nodes: FractureSurfaceNode[];
  vertices?: FractureSurfaceNode[];
  faces: number[][];
  cells: FractureSurfaceCell[];
  wings?: Record<'LEFT' | 'RIGHT', FractureSurfaceWingFrame>;
  frontCells: number[];
  morphology?: Record<string, number | string>;
  volumeDiagnostics?: Record<string, number | string | null>;
  couplingDiagnostics?: Record<string, unknown>;
  meshQuality?: Record<string, unknown>;
  proppantMapping?: string;
  junctions?: FractureJunctionV1[];
  branches?: NaturalFractureBranchV1[];
  branchDiagnostics?: Record<string, unknown>;
  fractureNetwork?: FractureNetworkV1;
  unifiedFractureNetwork?: UnifiedFractureNetworkV1;
  propagationFronts?: PropagationFrontV1[];
  ufmNumericalStatus?: Record<string, number | string | null>;
  networkDiagnostics?: Record<string, unknown>;
  surfaceProppantDiagnostics?: Record<string, unknown>;
  surfaceProppantClosureDiagnostics?: Record<string, unknown>;
  junctionProppant?: JunctionProppantStateV1[];
  branchProppant?: BranchProppantStateV1[];
  branchProppantDiagnostics?: Record<string, unknown>;
  branchProppantClosureDiagnostics?: Record<string, unknown>;
  branchMesh?: string;
};

/** 流量分配信息 */
export type FlowAllocation = {
  flow_rates_m3s: number[];
  uniformity: number;
  perf_frictions_mpa: number[];
};

/** 单段模拟结果 */
export type StageResult = {
  stage_number: number;
  perf_md: number;
  perf_tvd?: number;
  top_perf: number;
  bottom_perf: number;
  half_length: number;
  propped_half_length: number;
  height: number;
  height_growth_up?: number;
  height_growth_down?: number;
  w0_mm: number;
  w_avg_mm: number;
  net_pressure_mpa: number;
  efficiency_pct: number;
  conductivity: number;
  fluid_volume: number;
  proppant_mass: number;
  outline: [number, number][];
  mesh?: Mesh3D;
  stress_layers?: StressLayers;
  time_steps?: TimeStep[];   // Phase 1 新增
  // 多簇扩展 (Phase 2)
  multi_cluster?: boolean;
  geometry_definition?: {
    length_m: string;
    height_m: string;
    width_m: string;
  };
  n_clusters?: number;
  flow_allocation?: FlowAllocation;
  clusters?: ClusterResult[];
  mass_conservation?: {
    max_abs_error_m3s?: number;
    'all_steps_within_1e-9'?: boolean;
    step_count?: number;
  };
  cluster_formations?: Array<{
    tvd_m?: number;
    layer_name?: string | null;
    base_shmin_mpa?: number;
    upper_shmin_mpa?: number;
    lower_shmin_mpa?: number;
    seed_height_m?: number;
    seed_height_source?: 'explicit' | 'geomechanical_sublayer' | 'perforated_interval' | 'formation_fallback';
    seed_height_top_tvd_m?: number | null;
    seed_height_bottom_tvd_m?: number | null;
    seed_height_reference_name?: string | null;
    youngs_modulus_gpa?: number;
    poissons_ratio?: number;
    kic_mpa_sqrt_m?: number;
    input_metadata?: Record<string, unknown>;
  }>;
  seed_height_resolutions?: Array<{
    seed_height_m: number;
    seed_height_source: 'explicit' | 'geomechanical_sublayer' | 'perforated_interval' | 'formation_fallback';
    seed_height_top_tvd_m?: number | null;
    seed_height_bottom_tvd_m?: number | null;
    seed_height_reference_name?: string | null;
  }>;
  result_field_metadata?: Record<string, {
    support: string;
    source: string;
    unit: string;
  }>;
  effectiveness?: EffectivenessScore;
  diagnostics?: P3DDiagnostic[];
  confidence?: P3DConfidence;
  calibration_registry?: Record<string, {
    status?: 'available' | 'partial' | 'unavailable' | 'not_acquired' | 'not_interpreted';
    observations?: Record<string, unknown>;
    correction?: Record<string, unknown>;
  }>;
  calibration_metrics?: Record<string, unknown>;
};

/** 一次完整的压裂模拟 */
export type FracSimulation = {
  id: number;
  design: number;
  design_name: string;
  model_type: string;
  fluid_n: number;
  fluid_k: number;
  leakoff_c: number;
  youngs_modulus: number;
  poissons_ratio: number;
  frac_height: number;
  total_half_length: number;
  total_propped_length: number;
  avg_width: number;
  avg_efficiency: number;
  results: StageResult[];
  summary: Record<string, unknown> & {
    diagnostics?: P3DDiagnostic[];
    confidence?: P3DConfidence & { stage_levels?: Array<'High' | 'Medium' | 'Low'> };
    simulation_mode?: 'Predictive' | 'Calibrated';
    calibration_status?: 'Not Calibrated' | 'Partially Calibrated' | 'Field Calibrated';
    model_boundary?: string;
  };
  created_at: string;
};

/** 压裂方案 */
export type FracDesign = {
  id: number;
  name: string;
  is_active: boolean;
  stage_count: number;
  cluster_per_stage?: number;
  cluster_spacing?: number;
  stage_spacing?: number;
  pump_rate?: number;
  total_fluid?: number;
  total_proppant?: number;
  fluid_type?: string;
  proppant_type?: string;
  max_proppant_conc?: number;
  design_data?: Record<string, unknown>;
  stages?: Array<{
    stage_number: number;
    top_perf: number;
    bottom_perf: number;
    fluid_volume: number;
    proppant_mass: number;
    schedule?: Array<Record<string, unknown>>;
  }>;
};

/** 测斜点 */
export type SurveyPoint = {
  md: number;
  tvd: number;
  ns: number;
  ew: number;
  inclination?: number;
  azimuth?: number;
};

/** 地层分层 */
export type FormationLayerRow = {
  id: number;
  name: string;
  top_depth: number;
  bottom_depth: number;
  lithology: string;
  color_code: string;
  is_target?: boolean;
  dip_angle?: number;
  dip_azimuth?: number;
  extent_x?: number;
  extent_y?: number;
};

/** 地质建模层位网格；grid_z 为 TVD，按南向北、再西向东的行主序排列。 */
export type HorizonSurfaceRow = {
  id: number;
  name: string;
  nx: number;
  ny: number;
  origin_east: number;
  origin_north: number;
  dx: number;
  dy: number;
  grid_z: Array<number | null>;
  color_code?: string;
};

/** 模拟运行参数 */
export type SimulationOptions = {
  fluid_n: number;
  fluid_k: number;
  leakoff_c: number;
  youngs_modulus: number;
  poissons_ratio: number;
  frac_height: number;
  model_type: 'P3D' | 'PKN' | 'UFM';
  surface_model?: 'OFF' | 'ADAPTIVE_PLANAR_SURFACE_V1' | 'DIRECTIONAL_ADAPTIVE_SURFACE_V1';
  wing_mode?: 'SYMMETRIC_WINGS' | 'INDEPENDENT_WINGS_V1';
  max_direction_change_per_step?: number;
  surface_nx?: number;
  surface_nz?: number;
  max_front_advance_per_step?: number;
  n_steps?: number;
  n_clusters?: number;     // 簇数 (≥2 启用多簇模式)
  selected_stage_numbers?: number[];
  cluster_spacing_m?: number | null;
  pump_rate_m3min?: number | null;
  cluster_params?: Array<Record<string, unknown>>;
  p3d_seed_height_m?: number;
  natural_fracture_source_mode?: NaturalFractureSourceMode | 'GEOLOGICAL' | 'STOCHASTIC' | 'HYBRID';
  natural_fracture_design?: NaturalFractureDesignDTO;
  nf_search_margin_factor?: number;
  nf_random_seed?: number;
  natural_fracture_sets?: Array<Record<string, unknown>>;
  proppant_transport?: boolean;
  proppant_transport_model?: 'HEIGHT_AVERAGED_V1' | 'SURFACE_2D_V1';
  natural_fracture_branch?: boolean;
  branch_proppant_transport?: 'OFF' | 'ENGINEERING_V1';
  // 射孔参数 (多簇限流)
  perf_n?: number;
  perf_d_mm?: number;
  perf_cd?: number;
};

/** 压裂液效率颜色映射 */
export const effColor = (e: number): string =>
  e >= 60 ? '#1E8449' : e >= 40 ? '#2874A6' : e >= 25 ? '#D4770C' : '#C0392B';
